import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowLeft, RefreshCw, Lock, X, Send, Upload, Play } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 12000;

/**
 * PPV media picker — opens a modal to pick from the creator's vault and set a price.
 */
export const PPVMediaPicker = ({ creatorUsername, getAuthHeaders, onPick, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("free"); // "free" | "ppv"
  const [price, setPrice] = useState(10000);
  const [visibleCount, setVisibleCount] = useState(48); // show 48 initially, "ver más"
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`/api/admin/autopost/${creatorUsername}/media?limit=200`, {
          headers: { ...getAuthHeaders() },
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancel) setItems(d.items || []);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [creatorUsername, getAuthHeaders]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tier", "both");
      fd.append("source", "chat"); // private — NOT shown in feed nor autoposted
      const r = await fetch(`/api/admin/autopost/${creatorUsername}/media/upload`, {
        method: "POST",
        headers: { ...getAuthHeaders() }, // no Content-Type — let browser set boundary
        body: fd,
      });
      const d = await r.json();
      if (r.ok) {
        toast.success("✓ Subido a la bóveda");
        setItems((prev) => [d, ...prev]);
        setSelected(d);
      } else {
        toast.error(d.detail || "Error al subir");
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const confirm = () => {
    if (!selected) { toast.error("Selecciona un contenido"); return; }
    const finalPrice = mode === "free" ? 0 : parseInt(price, 10);
    if (mode === "ppv" && (!finalPrice || finalPrice < 1000)) {
      toast.error("Precio mínimo $1.000 COP");
      return;
    }
    onPick({
      mediaUrl: selected.mediaUrl,
      mediaType: selected.mediaType === "video" ? "video" : "photo",
      priceCOP: finalPrice,
    });
  };

  const visible = items.slice(0, visibleCount);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6">
      <div className="bg-zinc-950 border border-amber-400/30 rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
          <div>
            <h2 className="text-white font-bold flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-300" />
              Adjuntar contenido a @{creatorUsername}
            </h2>
            <p className="text-white/45 text-xs">Sube uno nuevo o elige de la bóveda</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode toggle + Upload */}
        <div className="px-5 pt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex p-1 bg-black/50 border border-white/10 rounded-xl">
            <button
              onClick={() => setMode("free")}
              data-testid="picker-mode-free"
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition ${
                mode === "free"
                  ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40"
                  : "text-white/55 hover:text-white"
              }`}
            >
              🎁 Gratis
            </button>
            <button
              onClick={() => setMode("ppv")}
              data-testid="picker-mode-ppv"
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition ${
                mode === "ppv"
                  ? "bg-amber-500/20 text-amber-200 border border-amber-400/40"
                  : "text-white/55 hover:text-white"
              }`}
            >
              🔒 PPV (de pago)
            </button>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="picker-upload-btn"
            className="px-3 py-1.5 bg-pink-500/15 hover:bg-pink-500/25 border border-pink-400/40 text-pink-200 text-xs font-bold rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploading ? "Subiendo…" : "Subir desde dispositivo"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            onChange={(e) => uploadFile(e.target.files?.[0])}
            className="hidden"
            data-testid="picker-file-input"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-white/40 text-sm py-12 text-center">Cargando bóveda…</p>
          ) : items.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-white/40 text-sm mb-3">Esta modelo no tiene contenido todavía.</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-pink-500/20 border border-pink-400/40 text-pink-200 text-sm font-bold rounded-lg inline-flex items-center gap-1.5"
              >
                <Upload className="w-4 h-4" /> Subir el primer archivo
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {visible.map((it) => {
                  const isSelected = selected?.id === it.id;
                  return (
                    <button
                      key={it.id}
                      onClick={() => setSelected(it)}
                      data-testid={`ppv-media-${it.id}`}
                      className={`aspect-square relative overflow-hidden rounded-lg border-2 transition bg-black/40 ${
                        isSelected
                          ? mode === "free"
                            ? "border-emerald-400 ring-2 ring-emerald-400/40"
                            : "border-amber-400 ring-2 ring-amber-400/40"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      {it.mediaType === "video" ? (
                        // Use a poster image if backend provides one, otherwise a placeholder.
                        // Never load video metadata in grid — that crashes the browser at scale.
                        <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-black flex items-center justify-center">
                          <Play className="w-7 h-7 text-white/70 drop-shadow" />
                          <span className="absolute top-1 right-1 text-[10px] bg-black/70 px-1.5 py-0.5 rounded font-bold">🎬</span>
                        </div>
                      ) : (
                        <img
                          src={it.mediaUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              {visibleCount < items.length && (
                <div className="text-center pt-3">
                  <button
                    onClick={() => setVisibleCount((c) => c + 48)}
                    className="text-xs text-white/60 hover:text-white px-4 py-1.5 rounded-full bg-white/5 border border-white/10"
                  >
                    Ver más ({items.length - visibleCount} restantes)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/10 bg-black/50 space-y-3">
          {mode === "ppv" && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold">Precio (COP)</label>
              <div className="flex gap-2 mt-1.5">
                {[5000, 10000, 25000, 50000].map((v) => (
                  <button
                    key={v}
                    onClick={() => setPrice(v)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition ${
                      price === v ? "bg-amber-500/20 border-amber-400/60 text-amber-200" : "bg-white/5 border-white/10 text-white/70"
                    }`}
                  >${v.toLocaleString("es-CO")}</button>
                ))}
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(parseInt(e.target.value, 10) || 0)}
                  min="1000"
                  step="1000"
                  className="w-24 bg-black/50 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-amber-400/50"
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-white/40">
              {selected
                ? mode === "free" ? "✓ Se enviará gratis" : `✓ PPV $${parseInt(price, 10).toLocaleString("es-CO")} COP`
                : "Toca un contenido"}
            </p>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-1.5 text-white/60 hover:text-white text-sm">Cancelar</button>
              <button
                onClick={confirm}
                disabled={!selected || (mode === "ppv" && price < 1000)}
                data-testid="ppv-picker-confirm"
                className={`px-5 py-1.5 text-white text-sm font-bold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5 ${
                  mode === "free"
                    ? "bg-gradient-to-r from-emerald-500 to-cyan-500"
                    : "bg-gradient-to-r from-amber-500 to-rose-500"
                }`}
              >
                {mode === "free" ? "🎁 Enviar gratis" : <><Lock className="w-3.5 h-3.5" /> Adjuntar PPV</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ChatThread — single conversation view for the admin.
 * Props:
 *   thread: { creatorUsername, subscriberEmail }
 *   getAuthHeaders, onClose, fullHeight (bool)
 *   onSent (optional: called after a successful reply)
 */
export const ChatThread = ({ thread, getAuthHeaders, onClose, fullHeight = false, onSent }) => {
  const { creatorUsername, subscriberEmail } = thread;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attached, setAttached] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    const r = await fetch(
      `/api/admin/jgfans/threads/${creatorUsername}/${encodeURIComponent(subscriberEmail)}`,
      { headers: { ...getAuthHeaders() } }
    );
    if (r.ok) {
      const d = await r.json();
      setMessages(d.messages || []);
    }
  }, [creatorUsername, subscriberEmail, getAuthHeaders]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    // Scroll only the messages container, not the whole window
    const el = endRef.current;
    if (!el) return;
    const container = el.parentElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim() && !attached) return;
    setSending(true);
    const text = input;
    const media = attached;
    setInput("");
    setAttached(null);
    try {
      const r = await fetch(
        `/api/admin/jgfans/threads/${creatorUsername}/${encodeURIComponent(subscriberEmail)}/reply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ text, media }),
        }
      );
      if (r.ok) {
        const m = await r.json();
        setMessages((prev) => [...prev, m]);
        onSent?.();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "No se pudo enviar");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`bg-white/5 border border-white/10 rounded-2xl flex flex-col ${fullHeight ? "h-full" : "h-[500px]"}`}>
      <div className="flex items-center gap-3 p-3 border-b border-white/10">
        <button onClick={onClose} className="text-white/60 hover:text-white md:hidden">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{subscriberEmail}</p>
          <p className="text-[11px] text-white/40">@{creatorUsername}</p>
        </div>
        <button onClick={load} className="text-white/40 hover:text-white" title="Actualizar">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 ? (
          <p className="text-center text-white/40 text-xs py-8">Sin mensajes aún</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`flex flex-col ${m.sender === "creator" ? "items-end" : "items-start"} gap-1`}
            >
              {m.media && (
                <div className="max-w-[200px] rounded-xl overflow-hidden border border-white/10 relative">
                  {m.media.mediaType === "video" ? (
                    <video src={m.media.mediaUrl} className="w-full" muted preload="metadata" />
                  ) : (
                    <img src={m.media.mediaUrl} alt="" className="w-full block" loading="lazy" decoding="async" />
                  )}
                  {parseInt(m.media.priceCOP || 0, 10) > 0 ? (
                    <>
                      <div className="absolute top-1 left-1 px-2 py-0.5 bg-black/70 backdrop-blur rounded-full text-[10px] font-bold text-amber-200 border border-amber-400/40">
                        🔒 PPV ${parseInt(m.media.priceCOP, 10).toLocaleString("es-CO")}
                      </div>
                      {Array.isArray(m.media.unlockedBy) && m.media.unlockedBy.includes(subscriberEmail.toLowerCase()) ? (
                        <div className="absolute bottom-1 right-1 px-2 py-0.5 bg-emerald-600/80 rounded-full text-[10px] font-bold text-white">
                          Desbloqueado ✓
                        </div>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Desbloquear manualmente este PPV para ${subscriberEmail}?`)) return;
                            const r = await fetch(`/api/admin/jgfans/messages/${m.id}/unlock-manual`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", ...getAuthHeaders() },
                              body: JSON.stringify({ email: subscriberEmail }),
                            });
                            if (r.ok) {
                              toast.success("✓ Desbloqueado");
                              load();
                            } else {
                              const d = await r.json().catch(() => ({}));
                              toast.error(d.detail || "Error");
                            }
                          }}
                          data-testid={`unlock-manual-${m.id}`}
                          className="absolute bottom-1 right-1 px-2 py-0.5 bg-emerald-500/30 hover:bg-emerald-500/50 backdrop-blur rounded-full text-[10px] font-bold text-emerald-100 border border-emerald-400/50"
                          title="Desbloquear manualmente (si el fan pagó pero no se reflejó)"
                        >
                          Desbloquear ✓
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="absolute top-1 left-1 px-2 py-0.5 bg-emerald-600/80 rounded-full text-[10px] font-bold text-white">
                      🎁 Gratis
                    </div>
                  )}
                </div>
              )}
              {m.text && (
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm whitespace-pre-wrap break-words ${
                    m.sender === "creator"
                      ? "bg-gradient-to-br from-pink-500 to-purple-500 text-white rounded-br-md"
                      : "bg-white/10 border border-white/10 text-white rounded-bl-md"
                  }`}
                >
                  {m.text}
                </div>
              )}
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      {attached && (
        <div className={`px-3 py-2 border-t flex items-center gap-2 ${
          attached.priceCOP > 0
            ? "border-amber-400/30 bg-amber-500/5"
            : "border-emerald-400/30 bg-emerald-500/5"
        }`}>
          <img src={attached.mediaUrl} alt="" className="w-10 h-10 rounded-md object-cover border border-white/10" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate font-semibold">
              {attached.mediaType === "video" ? "🎬 Video" : "📷 Foto"}
              {attached.priceCOP > 0 ? " · PPV" : " · Gratis"}
            </p>
            <p className={`text-[11px] ${attached.priceCOP > 0 ? "text-amber-200" : "text-emerald-200"}`}>
              {attached.priceCOP > 0
                ? `Precio: $${parseInt(attached.priceCOP, 10).toLocaleString("es-CO")} COP`
                : "Sin costo para el fan"}
            </p>
          </div>
          <button onClick={() => setAttached(null)} className="text-white/50 hover:text-red-400 p-1" title="Quitar">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-2 border-t border-white/10 flex gap-2">
        <button
          onClick={() => setPickerOpen(true)}
          data-testid="admin-attach-ppv"
          className="w-9 h-9 rounded-full bg-pink-500/15 border border-pink-400/40 text-pink-200 flex items-center justify-center hover:bg-pink-500/25 shrink-0"
          title="Adjuntar foto o video (gratis o PPV)"
        >
          <Lock className="w-4 h-4" />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={attached ? "Mensaje (opcional)…" : "Responder al fan…"}
          data-testid="admin-chat-input"
          className="flex-1 bg-black/40 border border-white/10 rounded-full px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-pink-400/50"
        />
        <button
          onClick={send}
          disabled={sending || (!input.trim() && !attached)}
          data-testid="admin-chat-send"
          className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center disabled:opacity-50 shrink-0"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {pickerOpen && (
        <PPVMediaPicker
          creatorUsername={creatorUsername}
          getAuthHeaders={getAuthHeaders}
          onPick={(media) => { setAttached(media); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatThread;
